import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { 
  Building2, Plus, Edit2, Trash2, MapPin, Calendar, Key, 
  Bot, GitBranch, BookOpen, Eye, EyeOff, Copy, Check, ExternalLink, Link2, RefreshCw, X,
  Wrench, Activity, AlertCircle
} from "lucide-react";

export default function Businesses() {
  const { t } = useTranslation();
  const [businesses, setBusinesses] = useState([]);
  const [agents, setAgents] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  
  // GHL Connection Doctor states
  const [doctor, setDoctor] = useState({ loading: false, data: null });
  const [oauthStatus, setOauthStatus] = useState({ connected: false, mode: "none", location_id: "" });
  const [oauthConnecting, setOauthConnecting] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    description: "",
    ghl_location_id: "",
    default_agent_id: "",
    default_calendar_id: "",
    calendar_custom_field: "calendario",
    openai_api_key: "",
    webhook_channels: ["ghl"],
  });

  useEffect(() => {
    // Redirect non-admins
    if (user && user.role !== "admin") {
      toast.error(t("biz.toastAdminRequired"));
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [bizRes, agentsRes] = await Promise.all([
        api.get("/businesses"),
        api.get("/agents"),
      ]);
      setBusinesses(bizRes.data);
      setAgents(agentsRes.data);
      
      // Calendars are per-business now - loaded when the edit dialog opens
    } catch (e) {
      toast.error(t("biz.toastLoadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      ghl_location_id: "",
      ghl_api_key: "",
      default_agent_id: "",
      default_calendar_id: "",
      calendar_custom_field: "calendario",
      openai_api_key: "",
      webhook_channels: ["ghl"],
    });
    setEditingBusiness(null);
    setShowApiKey(false);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (business) => {
    setEditingBusiness(business);
    setForm({
      name: business.name || "",
      description: business.description || "",
      ghl_location_id: business.ghl_location_id || "",
      ghl_api_key: business.ghl_api_key || "",
      default_agent_id: business.default_agent_id || "",
      default_calendar_id: business.default_calendar_id || "",
      calendar_custom_field: business.calendar_custom_field || "calendario",
      openai_api_key: business.openai_api_key || "",
      webhook_channels: business.webhook_channels || ["ghl"],
    });
    // Reset GHL Doctor state when opening dialog
    setDoctor({ loading: false, data: null });
    setOauthStatus({ connected: false, mode: "none", location_id: "" });
    // Fetch OAuth status for this business
    fetchOAuthStatusForBusiness(business.id);
    // Fetch this business's calendars (each location has its own)
    setCalendars([]);
    api.get("/ghl/calendars", { params: { business_id: business.id } })
      .then(r => setCalendars(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCalendars([]));
    setDialogOpen(true);
  };

  const fetchOAuthStatusForBusiness = async (businessId) => {
    if (!businessId) return;
    try {
      const { data } = await api.get(`/ghl/oauth/status?business_id=${businessId}`);
      setOauthStatus({
        connected: data.connected || false,
        mode: data.connected ? "oauth" : "none",
        location_id: data.location_id || "",
      });
    } catch {
      setOauthStatus({ connected: false, mode: "none", location_id: "" });
    }
  };

  const runDoctorForBusiness = async (businessId) => {
    if (!businessId) {
      toast.error(t("biz.toastNoBusiness"));
      return;
    }
    setDoctor({ loading: true, data: null });
    try {
      const { data } = await api.get(`/ghl/diagnostics?business_id=${businessId}`);
      setDoctor({ loading: false, data });
      if (data.overall_ok) {
        toast.success(t("biz.toastDoctorOk"));
      } else {
        toast.error(t("biz.toastDoctorIssues"));
      }
    } catch (e) {
      setDoctor({ loading: false, data: null });
      toast.error(t("biz.toastDoctorError"));
    }
  };

  const connectOAuthForBusiness = async (businessId) => {
    if (!businessId) {
      toast.error(t("biz.toastNoBusiness"));
      return;
    }
    setOauthConnecting(true);
    try {
      const { data } = await api.get(`/ghl/oauth/install-url?business_id=${businessId}`);
      window.open(data.install_url, "_blank");
      toast.success(t("biz.toastOauthOpening"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("biz.toastOauthFailed"));
    } finally {
      setOauthConnecting(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t("biz.toastNameRequired"));
      return;
    }

    try {
      const payload = { ...form };
      // Don't send masked API keys
      if (payload.openai_api_key && payload.openai_api_key.startsWith("***")) {
        delete payload.openai_api_key;
      }
      if (payload.ghl_api_key && payload.ghl_api_key.startsWith("***")) {
        delete payload.ghl_api_key;
      }
      // Convert "none" back to empty string for backend
      if (payload.default_agent_id === "none") payload.default_agent_id = "";
      if (payload.default_calendar_id === "none") payload.default_calendar_id = "";

      if (editingBusiness) {
        await api.put(`/businesses/${editingBusiness.id}`, payload);
        toast.success(t("biz.toastUpdated"));
      } else {
        await api.post("/businesses", payload);
        toast.success(t("biz.toastCreated"));
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(t("biz.toastSaveFailed"));
    }
  };

  const handleDelete = async (business) => {
    if (!confirm(t("biz.confirmDelete", { name: business.name }))) return;
    try {
      await api.delete(`/businesses/${business.id}`);
      toast.success(t("biz.toastDeleted"));
      fetchData();
    } catch (e) {
      toast.error(t("biz.toastDeleteFailed"));
    }
  };

  const copyWebhook = (channel, businessId) => {
    const url = `${BACKEND_URL}/api/webhooks/${channel}?business_id=${businessId}`;
    navigator.clipboard.writeText(url);
    setCopied(`${channel}-${businessId}`);
    toast.success(t("biz.webhookCopied"));
    setTimeout(() => setCopied(null), 2000);
  };

  // Admin preview: assign/clear a simulated plan to see the paying-customer UX
  const simulatePlan = async (businessId, plan) => {
    try {
      if (plan === "clear") {
        await api.post("/admin/simulate-plan", { business_id: businessId, action: "clear" });
        toast.success(t("biz.toastSimCleared"));
      } else {
        await api.post("/admin/simulate-plan", { business_id: businessId, plan });
        toast.success(t("biz.toastSimSet", { plan: plan === "agencia" ? "Small Agency" : "Personal" }));
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("biz.toastSimFailed"));
    }
  };

  const getBusinessAgents = (businessId) => {
    return agents.filter(a => a.business_id === businessId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="businesses-page" className="p-8 animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-['Manrope'] font-extrabold text-foreground">{t("biz.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("biz.subtitle")}</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-business-btn"
                onClick={openCreateDialog}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" /> {t("biz.addBusiness")}
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Manrope']">
                {editingBusiness ? t("biz.editBusiness") : t("biz.createBusiness")}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> {t("biz.basicInfo")}
                </h3>
                <div className="grid gap-4">
                  <div>
                    <Label>{t("biz.nameLabel")}</Label>
                    <Input
                      data-testid="business-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t("biz.namePh")}
                      className="bg-secondary/50 border-transparent"
                    />
                  </div>
                  <div>
                    <Label>{t("biz.descLabel")}</Label>
                    <Input
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder={t("biz.descPh")}
                      className="bg-secondary/50 border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* GHL Configuration */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {t("biz.ghlConfig")}
                </h3>
                <div className="grid gap-4">
                  <div>
                    <Label>GHL Location ID</Label>
                    <Input
                      data-testid="ghl-location-input"
                      value={form.ghl_location_id}
                      onChange={(e) => setForm({ ...form, ghl_location_id: e.target.value })}
                      placeholder="e.g., GpEg4wAuYiST4qW5qwD0"
                      className="bg-secondary/50 border-transparent font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {t("biz.locationIdHint")}
                    </p>
                  </div>
                  <div>
                    <Label>{t("biz.apiKeyLabel")}</Label>
                    <div className="relative">
                      <Input
                        data-testid="ghl-api-key-input"
                        type={showApiKey ? "text" : "password"}
                        value={form.ghl_api_key || ""}
                        onChange={(e) => setForm({ ...form, ghl_api_key: e.target.value })}
                        placeholder="pit-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        className="bg-secondary/50 border-transparent font-mono text-sm pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {t("biz.apiKeyHint")}
                    </p>
                  </div>
                </div>
              </div>

              {/* GHL Connection Doctor - Only show when editing */}
              {editingBusiness && (
                <div className="space-y-4 p-4 rounded-lg bg-blue-50/50 border border-blue-200/50">
                  <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-600" /> {t("biz.doctorTitle")}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t("biz.doctorDesc")}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => runDoctorForBusiness(editingBusiness.id)}
                      disabled={doctor.loading}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      data-testid="run-ghl-doctor-btn"
                    >
                      <Wrench className="w-4 h-4 mr-2" />
                      {doctor.loading ? t("biz.doctorRunning") : t("biz.doctorRun")}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => connectOAuthForBusiness(editingBusiness.id)}
                      disabled={oauthConnecting}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                      data-testid="connect-oauth-btn"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      {oauthConnecting ? t("biz.oauthGenerating") : t("biz.oauthConnect")}
                    </Button>
                  </div>

                  {/* OAuth Status */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">{t("biz.oauthStatus")}:</span>
                      <Badge variant="outline" className={oauthStatus.connected ? "border-green-500 text-green-600" : "border-red-500 text-red-600"}>
                        {oauthStatus.connected ? t("biz.connected") : t("biz.notConnected")}
                      </Badge>
                    </div>
                  </div>

                  {/* Doctor Results */}
                  {doctor.data?.checks?.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {doctor.data.checks.map((check) => (
                        <div key={check.id} className="p-2 rounded bg-white/80 flex items-center justify-between gap-2 text-xs">
                          <div>
                            <span className="font-medium">{check.label}</span>
                            <p className="text-muted-foreground text-[10px] truncate max-w-[300px]">{check.detail}</p>
                          </div>
                          <Badge variant="outline" className={check.ok ? "border-green-500 text-green-600" : "border-red-500 text-red-600"}>
                            {check.ok ? "OK" : "FAIL"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Agent & Calendar */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Bot className="w-4 h-4" /> {t("biz.agentCalendar")}
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>{t("biz.defaultAgent")}</Label>
                    <Select 
                      value={form.default_agent_id} 
                      onValueChange={(v) => setForm({ ...form, default_agent_id: v })}
                    >
                      <SelectTrigger className="bg-secondary/50 border-transparent">
                        <SelectValue placeholder={t("biz.selectAgent")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("biz.none")}</SelectItem>
                        {agents.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("biz.defaultCalendar")}</Label>
                    <Select 
                      value={form.default_calendar_id} 
                      onValueChange={(v) => setForm({ ...form, default_calendar_id: v })}
                    >
                      <SelectTrigger className="bg-secondary/50 border-transparent">
                        <SelectValue placeholder={t("biz.selectCalendar")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("biz.none")}</SelectItem>
                        {calendars.filter(c => c.isActive).map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label>{t("biz.calendarField")}</Label>
                    <Input
                      value={form.calendar_custom_field}
                      onChange={(e) => setForm({ ...form, calendar_custom_field: e.target.value })}
                      placeholder="calendario"
                      className="bg-secondary/50 border-transparent font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {t("biz.calendarFieldHint")}
                    </p>
                  </div>
                </div>
              </div>

              {/* OpenAI API Key */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Key className="w-4 h-4" /> {t("biz.openaiTitle")}
                </h3>
                <div>
                  <Label>{t("biz.bizApiKeyLabel")}</Label>
                  <div className="flex gap-2">
                    <Input
                      type={showApiKey ? "text" : "password"}
                      value={form.openai_api_key}
                      onChange={(e) => setForm({ ...form, openai_api_key: e.target.value })}
                      placeholder={t("biz.bizApiKeyPh")}
                      className="bg-secondary/50 border-transparent font-mono text-sm"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="shrink-0"
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    {t("biz.bizApiKeyHint")}
                  </p>
                </div>
              </div>

              {/* GHL Embed URL - Only show when editing */}
              {editingBusiness && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Link2 className="w-4 h-4" /> {t("biz.embedTitle")}
                  </h3>
                  <div>
                    <Label>{t("biz.embedLabel")}</Label>
                    {editingBusiness.embed_token ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={`${window.location.origin}/embed?token=${editingBusiness.embed_token}${editingBusiness.ghl_location_id ? `&location_id=${editingBusiness.ghl_location_id}` : ''}`}
                            className="bg-secondary/50 border-transparent font-mono text-xs"
                          />
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => {
                              const url = `${window.location.origin}/embed?token=${editingBusiness.embed_token}${editingBusiness.ghl_location_id ? `&location_id=${editingBusiness.ghl_location_id}` : ''}`;
                              navigator.clipboard.writeText(url);
                              setCopied('embed');
                              setTimeout(() => setCopied(null), 2000);
                              toast.success(t("biz.embedCopied"));
                            }}
                            className="shrink-0"
                          >
                            {copied === 'embed' ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await api.post(`/businesses/${editingBusiness.id}/generate-embed-token`);
                              setEditingBusiness({ ...editingBusiness, embed_token: res.data.embed_token });
                              toast.success(t("biz.embedTokenGenerated"));
                              fetchData();
                            } catch (e) {
                              toast.error(t("biz.embedTokenFailed"));
                            }
                          }}
                          className="text-xs"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> {t("biz.embedRegenerate")}
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Button 
                          variant="outline" 
                          onClick={async () => {
                            try {
                              const res = await api.post(`/businesses/${editingBusiness.id}/generate-embed-token`);
                              setEditingBusiness({ ...editingBusiness, embed_token: res.data.embed_token });
                              toast.success(t("biz.embedTokenGenerated"));
                              fetchData();
                            } catch (e) {
                              toast.error("Failed to generate token");
                            }
                          }}
                        >
                          <Link2 className="w-4 h-4 mr-2" /> {t("biz.embedGenerate")}
                        </Button>
                        <p className="text-xs text-muted-foreground/60 mt-2">
                          {t("biz.embedHint")}
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground/60 mt-2">
                      {t("biz.embedUseHint")}
                    </p>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  {t("biz.cancel")}
                </Button>
                <Button 
                  data-testid="save-business-btn"
                  onClick={handleSave}
                  className="bg-primary text-primary-foreground"
                >
                  {editingBusiness ? t("biz.updateBusiness") : t("biz.createBusinessBtn")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Businesses Grid */}
      {businesses.length === 0 ? (
        <Card className="bg-white border-gray-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Building2 className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">{t("biz.emptyTitle")}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {isAdmin ? t("biz.emptyAdmin") : t("biz.emptyUser")}
            </p>
            {isAdmin && (
              <Button onClick={openCreateDialog} className="bg-primary text-primary-foreground">
                <Plus className="w-4 h-4 mr-2" /> {t("biz.addBusiness")}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {businesses.map((business) => {
            const businessAgents = getBusinessAgents(business.id);
            return (
              <Card key={business.id} className="bg-white border-gray-200 hover:border-primary/30 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-['Manrope']">{business.name}</CardTitle>
                        <Badge 
                          variant="outline" 
                          className={business.status === "active" ? "border-primary/30 text-primary" : "border-muted text-muted-foreground"}
                        >
                          {business.status === "active" ? t("biz.statusActive") : (business.status || t("biz.statusActive"))}
                        </Badge>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => openEditDialog(business)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(business)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {business.description && (
                    <p className="text-sm text-muted-foreground">{business.description}</p>
                  )}
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <Bot className="w-4 h-4 mx-auto mb-1 text-primary" />
                      <span className="text-xs text-muted-foreground">{businessAgents.length} {t("biz.agentsCount")}</span>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <MapPin className="w-4 h-4 mx-auto mb-1 text-[#FFD60A]" />
                      <span className="text-xs text-muted-foreground">
                        {business.ghl_location_id ? "GHL" : t("biz.ghlNo")}
                      </span>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <Calendar className="w-4 h-4 mx-auto mb-1 text-[#0A84FF]" />
                      <span className="text-xs text-muted-foreground">
                        {business.default_calendar_id ? t("biz.calYes") : t("biz.calNo")}
                      </span>
                    </div>
                  </div>

                  {/* GHL Location */}
                  {business.ghl_location_id && (
                    <div className="p-2 rounded-lg bg-secondary/20">
                      <p className="text-xs text-muted-foreground mb-1">GHL Location ID</p>
                      <code className="text-xs text-primary font-mono">{business.ghl_location_id}</code>
                    </div>
                  )}

                  {/* Webhooks */}
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Webhooks</p>
                    <div className="flex flex-wrap gap-1">
                      {(business.webhook_channels || ["ghl"]).map(channel => (
                        <Button
                          key={channel}
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => copyWebhook(channel, business.id)}
                        >
                          {copied === `${channel}-${business.id}` ? (
                            <Check className="w-3 h-3 mr-1 text-primary" />
                          ) : (
                            <Copy className="w-3 h-3 mr-1" />
                          )}
                          {channel.toUpperCase()}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Admin preview: simulate a paid plan to see the customer UX */}
                  {isAdmin && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        {t("biz.clientView")}{business.billing?.simulated ? t("biz.clientViewSim") : ""}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {business.billing ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs border-amber-400 text-amber-600"
                            onClick={() => simulatePlan(business.id, "clear")}
                          >
                            <X className="w-3 h-3 mr-1" />
                            {business.billing.simulated ? t("biz.removePlanSim") : t("biz.removePlan")}
                          </Button>
                        ) : (
                          <>
                            <Button variant="outline" size="sm" className="h-7 text-xs"
                              onClick={() => simulatePlan(business.id, "solo")}>
                              {t("biz.simPersonal")}
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs"
                              onClick={() => simulatePlan(business.id, "agencia")}>
                              {t("biz.simAgency")}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Whitelabel activation link (embed) — copy with one click */}
                  {business.embed_token && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">{t("biz.whitelabelLink")}</p>
                      <div className="flex flex-wrap gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs border-primary/40 text-primary"
                          data-testid={`copy-embed-${business.id}`}
                          onClick={() => {
                            const url = `${window.location.origin}/embed?token=${business.embed_token}`;
                            navigator.clipboard.writeText(url);
                            setCopied(`embed-${business.id}`);
                            toast.success(t("biz.activationCopied"));
                            setTimeout(() => setCopied(null), 2000);
                          }}
                        >
                          {copied === `embed-${business.id}` ? (
                            <Check className="w-3 h-3 mr-1" />
                          ) : (
                            <Link2 className="w-3 h-3 mr-1" />
                          )}
                          Whitelabel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
